/**
 * BrandingProvider — Wraps the app to provide white-label theming context.
 *
 * On mount, fetches branding for the current hostname and applies CSS
 * custom properties. Child components can access branding via useBranding().
 */

import {
  createContext,
  useContext,
  createEffect,
  type JSX,
  type Accessor,
} from "solid-js";
import {
  createBrandingResource,
  getEffectiveBranding,
  applyBrandingCss,
  DEFAULT_BRANDING,
  type BrandingConfig,
  type BrandingResolveResult,
} from "../services/branding";

// ============================================================================
// Context
// ============================================================================

interface BrandingContextValue {
  /** Current effective branding config */
  branding: Accessor<BrandingConfig>;
  /** Whether the site is using custom (non-default) branding */
  isBranded: Accessor<boolean>;
  /** The resolved account ID (if custom domain) */
  accountId: Accessor<string | undefined>;
  /** Whether branding is still loading */
  loading: Accessor<boolean>;
  /** Force re-fetch branding from API */
  refetch: () => void;
  /** Set a local preview override (for BrandingSettings live preview) */
  setPreview: (config: BrandingConfig) => void;
  /** Clear the local preview override */
  clearPreview: () => void;
}

const BrandingContext = createContext<BrandingContextValue>();

// ============================================================================
// Provider
// ============================================================================

export function BrandingProvider(props: { children: JSX.Element }) {
  const { branding: brandingResource, refetch, setPreview, clearPreview } =
    createBrandingResource();

  const effectiveBranding = () => {
    const resolved = brandingResource();
    return getEffectiveBranding(resolved);
  };

  // Apply CSS custom properties whenever branding changes
  createEffect(() => {
    applyBrandingCss(effectiveBranding());
  });

  const contextValue: BrandingContextValue = {
    branding: effectiveBranding,
    isBranded: () => {
      const resolved = brandingResource();
      return resolved?.branded ?? false;
    },
    accountId: () => {
      const resolved = brandingResource();
      return resolved?.accountId;
    },
    loading: () => brandingResource.loading,
    refetch,
    setPreview,
    clearPreview,
  };

  return (
    <BrandingContext.Provider value={contextValue}>
      {props.children}
    </BrandingContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Access the current branding context.
 * Must be used within a BrandingProvider.
 */
export function useBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    // Return safe defaults if used outside provider (shouldn't happen)
    return {
      branding: () => DEFAULT_BRANDING,
      isBranded: () => false,
      accountId: () => undefined,
      loading: () => false,
      refetch: () => {},
      setPreview: () => {},
      clearPreview: () => {},
    };
  }
  return ctx;
}
